import { useEffect, useState } from 'react';
import { useGameStore } from '@/ui/store/gameStore';
import { saveManager } from '@/save';

/**
 * FabTutorialArrow — animowana strzałka wskazująca FAB menu (sesja 30).
 *
 * Pokazywany TYLKO przy pierwszym uruchomieniu po pełnym onboarding (gracz
 * wybrał avatar + 2 startery + Phaser wystartował). Pomaga graczowi (głównie
 * dziecku) zauważyć FAB w prawym dolnym rogu — bez tego mógłby przegapić
 * menu i nie wiedzieć gdzie są jajka/pety.
 *
 * **Hide triggers (cokolwiek pierwsze):**
 *  - Klik FAB → ActionMenu sam ustawia `setHasSeenFabTutorial(true)`,
 *    ten komponent reaguje przez Zustand listener
 *  - 12s timeout — nawet jeśli gracz nie kliknie, tutorial znika żeby nie
 *    blokować view forever. Save flag też ustawiony żeby nie pokazać znowu.
 *  - Modal otwarty (`openModal !== null`) — tutorial schowany podczas
 *    interakcji z innymi UI
 *
 * **Animacja:**
 *  - Static PNG (`ui_arrow_tutorial.png`) — diagonal pointing bottom-right
 *  - CSS `animate-bounce` Tailwind (built-in) — vertical bounce 0.5s loop
 *  - Plus własne keyframes `tutorialPulse` — subtle scale 1.0 ↔ 1.08 co 1.5s
 *    (drugi layer animacji dla "alive" feel)
 *
 * **Position:** absolute, offset od FAB (prawy dolny róg App.tsx). Strzałka
 * jest na lewo-górno od FAB-a — wskazuje diagonalnie w prawo-dół.
 */

const HIDE_AFTER_MS = 12_000;

export function FabTutorialArrow(): JSX.Element | null {
  const hasSeenFabTutorial = useGameStore((s) => s.hasSeenFabTutorial);
  const setHasSeenFabTutorial = useGameStore((s) => s.setHasSeenFabTutorial);
  const openModal = useGameStore((s) => s.openModal);
  const [timedOut, setTimedOut] = useState(false);

  // Timeout — po 12s automatycznie hide + zapisz flag (gracz przegapił, nie
  // pokazujemy znowu).
  useEffect(() => {
    if (hasSeenFabTutorial) return;
    const timer = setTimeout(() => {
      setTimedOut(true);
      setHasSeenFabTutorial(true);
      void persistTutorialFlag();
    }, HIDE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [hasSeenFabTutorial, setHasSeenFabTutorial]);

  // Hide gdy: flag już true, timeout, albo otwarty modal (gracz interaguje
  // z UI — tutorial by przykrywał).
  if (hasSeenFabTutorial || timedOut || openModal !== null) {
    return null;
  }

  return (
    <>
      {/* Position: absolute, prawy dolny róg ale offset W LEWO i W GÓRĘ od FAB,
          żeby strzałka WSKAZYWAŁA na FAB (diagonalnie w prawo-dół).
          FAB jest w `right-3 bottom-3` = 12px margin, z width 56px (w-14).
          Strzałka 100×100 px, ulokowana offsetem ~80px w lewo i w górę od FAB. */}
      <div
        className="absolute pointer-events-none z-30"
        style={{
          right: '80px',
          bottom: '80px',
        }}
        aria-hidden="true"
      >
        <img
          src="/ui/ui_arrow_tutorial.webp"
          alt=""
          className="w-24 h-24 object-contain"
          style={{
            // Sesja 30 POST review LOW (Codex F): obie animacje w jednym
            // `animation` shorthand. Wcześniej `className='animate-bounce'`
            // (Tailwind) był nadpisany przez inline `style.animation` —
            // wykonywał się tylko tutorialPulse. Teraz oba layery działają
            // równolegle: vertical bounce (Tailwind keyframes) + subtle scale
            // pulse. Daje "podwójną" energię ruchu, gracz nie przegapi.
            animation: 'bounce 1s infinite, tutorialPulse 1.5s ease-in-out infinite',
          }}
          draggable={false}
          onError={(e) => {
            // Defensive: jeśli PNG nie istnieje (gracz nie wgrał yet), hide.
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      <style>{`
        @keyframes tutorialPulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.08); }
        }
      `}</style>
    </>
  );
}

async function persistTutorialFlag(): Promise<void> {
  await saveManager.update((current) => {
    if (current.flags.hasSeenFabTutorial === true) return current;
    return {
      ...current,
      flags: { ...current.flags, hasSeenFabTutorial: true },
    };
  });
}
